import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Product } from "../../../../../../models/product";
import CurrencyService from "../../../../../../services/currency.service";
import CustomerService from "../../../../../../services/customer.service";
import ProductService from "../../../../../../services/product.service";
import SupplierService from "../../../../../../services/supplier.service";
import UserCompanyService from "../../../../../../services/user.company.service";
import { checkError } from "../../../../../../util/checkError";
import { Status, TypeError } from "../../../../../../util/enums";
import { CurrenciesKeys, ProductKeys, SupplierKeys } from "../../../../../../util/queries";
import { ProductChangeKeys } from "../types";

interface SaveProductProps {
  id?: number;
  product: Product;
  overrideChangedOptions?: ProductChangeKeys;
  uuid?: string;
}

const CACHE_TIME = 60 * 60 * 1000; // 1 hour
const useAction = () => {
  const { currentUser } = useAppState();
  const history = useHistory();
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();
  const [requestingSaveProduct, setRequestingSaveProduct] = useState(false);

  const getProductById = (ProductId: number, callerCallback?: (ProductId: number) => void) => {
    if (ProductId && callerCallback) callerCallback(ProductId);

    return useQuery({
      queryKey: ProductKeys.getById(ProductId),
      queryFn: async () => {
        const response = await ProductService.getById(Number(ProductId), {
          include: JSON.stringify([
            { association: "projects" },
            { association: "picture" },
            {
              association: "cartons",
              attributes: ["id"],
              include: [
                { association: "shipments", attributes: ["id", "statusShipment"], required: true },
              ],
            },
            {
              association: "purchaseOrders",
              attributes: ["id"],
              include: [{ association: "project", attributes: ["name"] }],
            },
          ]),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!ProductId,
      cacheTime: CACHE_TIME,
    });
  };

  const useSaveProduct = (callback?: () => void) => {
    return useMutation({
      mutationKey: ProductKeys.save(),
      mutationFn: ({ id, product }: SaveProductProps) => {
        if (id)
          return ProductService.update(id, {
            ...product,
          });

        return ProductService.create({ ...product, companyId: currentUser?.companyId });
      },
      retry: 0,
      networkMode: "online",
      onMutate: () => setRequestingSaveProduct(true),
      onError: (error) => {
        setRequestingSaveProduct(false);
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: (response, variables) => {
        const requestWasBlocked = response?.data?.type === TypeError.ERROR_WITHOUT_TOAST;

        if (requestWasBlocked) return;
        setRequestingSaveProduct(false);

        queryClient.invalidateQueries({ queryKey: [ProductKeys.all, variables.id] });
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
        history.push("/app/products");
      },
    });
  };

  const useDeleteProduct = (id: number, callback?: () => void) => {
    return useMutation({
      mutationKey: ProductKeys.delete(id),
      mutationFn: (id: number) => {
        return ProductService.delete(id);
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [ProductKeys.all, id] });
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
        history.push("/app/products");
      },
    });
  };

  const getAllCompanies = () => {
    return useQuery({
      queryKey: ["user.companies.list"],
      queryFn: async () => {
        const response = await UserCompanyService.getAll({
          userId: currentUser?.id,
          status: Status.ACTIVE,
          include: JSON.stringify([
            {
              association: "company",
              order: [["name", "asc"]],
              where: { status: Status.ACTIVE },
            },
          ]),
        });

        if (response && response.data) return response.data;
      },
      refetchOnWindowFocus: false,
      cacheTime: CACHE_TIME,
    });
  };

  const getAllSuppliers = () => {
    return useQuery({
      queryKey: [SupplierKeys.all, currentUser?.companyId],
      queryFn: async () => {
        const response = await SupplierService.getAll({
          active: 1,
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["name", "asc"]]),
          attributes: ["name", "id"],
        });

        if (response && response.data) return response.data;
      },
      refetchOnWindowFocus: false,
      cacheTime: CACHE_TIME,
    });
  };

  const getAllCustomers = () => {
    return useQuery({
      queryKey: [SupplierKeys.all, currentUser?.companyId],
      queryFn: async () => {
        const response = await CustomerService.getAll({
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
          sortBy: JSON.stringify([["name", "ASC"]]),
        });

        if (response && response.data) return response.data;
      },
      refetchOnWindowFocus: false,
      cacheTime: CACHE_TIME,
    });
  };

  const getAllCurrencies = () => {
    return useQuery({
      queryKey: CurrenciesKeys.getAll([currentUser?.companyId, Status.ACTIVE]),
      queryFn: async () => {
        const response = await CurrencyService.getAll({
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
        });

        if (response && response.data) return response.data;
      },
      refetchOnWindowFocus: false,
      cacheTime: CACHE_TIME,
    });
  };

  return {
    requestingSaveProduct,
    getProductById,
    useSaveProduct,
    useDeleteProduct,
    getAllCompanies,
    getAllCustomers,
    getAllSuppliers,
    getAllCurrencies,
  };
};

export default useAction;
